import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import NewsCard from "./NewsCard";
import { useLang, pickTitle, pickSummary, timeAgo, SECTIONS, imgSrc } from "../lib/i18n";

export function KineticLines({ text, perLine = 4, delay = 0.15, className = "" }) {
  return (
    <motion.span
      className={`block will-change-transform ${className}`}
      initial={{ y: "40%", opacity: 0 }}
      animate={{ y: "0%", opacity: 1 }}
      transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  );
}

export default function Hero({ hero, top }) {
  const { lang, t } = useLang();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const y = useTransform(scrollYProgress, [0, 1], [0, -36]);

  if (!hero) return null;
  const sec = SECTIONS.find((s) => s.id === hero.category);
  const secLabel = sec && (lang === "hi" ? sec.hi : sec.en);

  if (!hero.image_url) {
    return (
      <section className="grid grid-cols-12 gap-6" data-testid="hero-bento">
        <div className="col-span-12 lg:col-span-8">
          <Link to={`/article/${hero.id}`} data-testid="hero-main-story" className="group block border-2 border-ink p-6 md:p-10 bg-white">
            <span className="inline-flex items-center gap-2.5 font-mono text-[10px] md:text-xs font-bold uppercase tracking-[0.22em] text-deepgreen">
              <span className="bg-brk text-paper px-2 py-0.5">{t.topStory}</span>
              {secLabel}
            </span>
              <h1
                data-testid="hero-headline"
                className="mt-4 font-hindi text-3xl md:text-5xl leading-[1.35] group-hover:text-deepgreen transition-colors"
              >
                {pickTitle(hero, lang)}
              </h1>
              <span className="block gold-rule w-24 mt-4" />
              <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                {pickSummary(hero, lang)}
              </p>
              <div className="mt-3 font-mono text-[11px] text-muted-foreground">
                {hero.author} • {timeAgo(hero.published_at, lang)}
              </div>
          </Link>
        </div>
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-7">
          {(top || []).slice(0, 2).map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 + i * 0.18, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <NewsCard article={a} variant="standard" />
            </motion.div>
          ))}
          <div>
            {(top || []).slice(2, 4).map((a) => (
              <NewsCard key={a.id} article={a} variant="list" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="grid grid-cols-12 gap-6" data-testid="hero-bento">
      <div className="col-span-12 lg:col-span-8">
        <Link to={`/article/${hero.id}`} data-testid="hero-main-story" className="group block">
          <div ref={imgRef} className="relative overflow-hidden border-2 border-ink">
            <motion.img
              src={imgSrc(hero.image_url)}
              alt={pickTitle(hero, lang)}
              style={{ scale, y }}
              className="w-full aspect-video object-cover will-change-transform"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8">
              <span className="inline-flex items-center gap-2.5 font-mono text-[10px] md:text-xs font-bold uppercase tracking-[0.22em] text-gold-light">
                <span className="bg-brk text-paper px-2 py-0.5">{t.topStory}</span>
                {secLabel}
              </span>
              <h1
                data-testid="hero-headline"
                className="mt-3 text-paper font-hindi text-3xl md:text-5xl leading-[1.35] drop-shadow-md"
              >
                <KineticLines text={pickTitle(hero, lang)} />
              </h1>
            </div>
          </div>
          <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
            {pickSummary(hero, lang)}
          </p>
          <div className="mt-1.5 font-mono text-[11px] text-muted-foreground">
            {hero.author} • {timeAgo(hero.published_at, lang)}
          </div>
        </Link>
      </div>

      <div className="col-span-12 lg:col-span-4 flex flex-col gap-7">
        {(top || []).slice(0, 2).map((a, i) => (
          <motion.div
            key={a.id}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 + i * 0.18, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <NewsCard article={a} variant="standard" />
          </motion.div>
        ))}
        <div>
          {(top || []).slice(2, 4).map((a) => (
            <NewsCard key={a.id} article={a} variant="list" />
          ))}
        </div>
      </div>
    </section>
  );
}
